import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Download, FilterX, Laptop, RotateCcw, StickyNote, UserCheck, X } from 'lucide-react';
import { getAssetDefenderBadges, listAssets, restoreAsset } from '@/api/assets';
import { ApiError } from '@/api/client';
import { getLocations } from '@/api/locations';
import { useAuth } from '@/auth/useAuth';
import { assetCategories, lifecycleStates } from './schema';
import { useSort, type SortDir } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import { WarrantyExpiry } from '@/components/ui/WarrantyExpiry';
import { isActiveDevice } from '@/lib/deviceActivity';
import { buildAssetsCsv } from './exportCsv';
import { DesktopTable, MobileCards, MobileCard, CardField } from '@/components/common/responsiveList';
import { MobileFilterPanel, MobileFilterField } from '@/components/common/mobileControls';
import { PersonPicker } from '@/components/common/assignPickers';
import { MultiSelectFilter } from '@/components/common/MultiSelectFilter';
import { BulkAssignDialog } from './BulkAssignDialog';
import { rowBadge, type RowBadge } from './rowDefender';

const PAGE_SIZE = 50;
// +1 for the selection checkbox, +1 for State. (Asset tag is searchable but is not a column.)
const COLUMN_COUNT = 13;
// Backend caps pageSize at 200; we page through at that size to export every match.
const EXPORT_PAGE_SIZE = 200;

// Filter/sort/page state is mirrored to sessionStorage so returning from an
// asset detail (back button) restores the exact filtered view the user left.
const FILTERS_KEY = 'oscar.assets.filters';

interface FilterSnapshot {
  search?: string; serial?: string; hostname?: string; makeModel?: string;
  category?: string[]; lifecycle?: string[]; location?: string[]; intune?: string;
  warranty?: '' | 'expiring' | 'expired'; note?: '' | 'has' | 'none';
  activity?: '' | 'active' | 'inactive'; mismatch?: boolean; vsAssignee?: boolean; intuneUnassigned?: boolean;
  arc?: ArcFilter;
  defender?: DefenderFilter;
  assignedTo?: string; assignedToName?: string;
  sortKey?: string; sortDir?: SortDir; page?: number;
}

type ArcFilter = '' | 'servers' | 'defender' | 'nodefender';
const ARC_FILTERS: ArcFilter[] = ['servers', 'defender', 'nodefender'];
type DefenderFilter = '' | 'healthy' | 'attention' | 'unknown';
const DEFENDER_FILTERS: DefenderFilter[] = ['healthy', 'attention', 'unknown'];

function loadSnapshot(): FilterSnapshot {
  try {
    return JSON.parse(sessionStorage.getItem(FILTERS_KEY) ?? '{}') as FilterSnapshot;
  } catch {
    return {};
  }
}

function useDebounced<T>(value: T, delay = 300): T {
  const [d, setD] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setD(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return d;
}

// Compact controls for the per-column filter row.
const filterCls = 'w-full rounded border border-border bg-white px-1.5 py-1 text-xs font-normal normal-case tracking-normal text-darkGrey';

export function AssetsRegister() {
  const { canMutate, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  // Recovery view: surface soft-deleted assets so an admin can restore one
  // (a deleted asset still holds its serial under the unique index, blocking a
  // new asset with that serial until it's restored).
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  // Restored once on mount; URL params (Dashboard deep-links) win over the snapshot.
  const snap = useRef(loadSnapshot()).current;

  // Global search (top).
  const [searchInput, setSearchInput] = useState(snap.search ?? '');
  // Per-column text filters (debounced).
  const [serialIn, setSerialIn] = useState(snap.serial ?? '');
  const [hostnameIn, setHostnameIn] = useState(snap.hostname ?? '');
  const [makeModelIn, setMakeModelIn] = useState(snap.makeModel ?? '');
  // Per-column select filters.
  // Multi-select (Marty, 01/09/2026). Empty array = no filter; the API takes a
  // comma-separated list and a single value still behaves as it always did.
  const [category, setCategory] = useState<string[]>(snap.category ?? []);
  const [lifecycle, setLifecycle] = useState<string[]>(snap.lifecycle ?? []);
  const [location, setLocation] = useState<string[]>(snap.location ?? []);
  const [intune, setIntune] = useState(snap.intune ?? '');
  const [warranty, setWarranty] = useState<'' | 'expiring' | 'expired'>(snap.warranty ?? '');
  const [note, setNote] = useState<'' | 'has' | 'none'>(snap.note ?? '');
  // Activity + primary-user-mismatch seed from the URL so the Dashboard KPI tiles deep-link in.
  const [activity, setActivity] = useState<'' | 'active' | 'inactive'>(() => {
    const a = searchParams.get('activity');
    return a === 'active' || a === 'inactive' ? a : (snap.activity ?? '');
  });
  const [mismatch, setMismatch] = useState(() =>
    searchParams.get('primaryUserMismatch') === '1' ? true : (snap.mismatch ?? false));
  // Primary user vs the person it is assigned to (Marty, 29/10/2026) - seeded from the URL like the tile above.
  const [vsAssignee, setVsAssignee] = useState(() =>
    searchParams.get('primaryUserVsAssignee') === '1' ? true : (snap.vsAssignee ?? false));

  // Azure Arc servers (Anthony, 01/11/2026). Seeded from the URL so a link can open the list already filtered.
  const [arc, setArc] = useState<ArcFilter>(() => {
    const a = searchParams.get('arc') as ArcFilter;
    return ARC_FILTERS.includes(a) ? a : (snap.arc ?? '');
  });

  // Defender health Intune reports for Windows devices (Anthony, 01/11/2026). Seeded from the URL like the Arc filter.
  const [defender, setDefender] = useState<DefenderFilter>(() => {
    const d = searchParams.get('defender') as DefenderFilter;
    return DEFENDER_FILTERS.includes(d) ? d : (snap.defender ?? '');
  });

  // Intune knows who uses it, Oscar does not (Anthony, 01/11/2026) - seeded from the URL like the tiles above.
  const [intuneUnassigned, setIntuneUnassigned] = useState(() =>
    searchParams.get('intuneUserUnassigned') === '1' ? true : (snap.intuneUnassigned ?? false));

  // Assigned-to filter. Marty's offboarding flow starts here: find everything a
  // leaver holds, select it all, reassign in one step.
  const [assignedTo, setAssignedTo] = useState(snap.assignedTo ?? '');
  const [assignedToName, setAssignedToName] = useState(snap.assignedToName ?? '');

  const [page, setPage] = useState(snap.page ?? 1);
  const reset = () => setPage(1);

  const search = useDebounced(searchInput);
  const serial = useDebounced(serialIn);
  const hostname = useDebounced(hostnameIn);
  const makeModel = useDebounced(makeModelIn);

  const { sortKey, sortDir, onSort } = useSort(snap.sortKey ?? 'category', snap.sortDir ?? 'asc');
  const handleSort = (k: string) => { onSort(k); reset(); };

  // Persist the live filter/sort/page state for back-navigation restore.
  useEffect(() => {
    const s: FilterSnapshot = {
      search: searchInput, serial: serialIn, hostname: hostnameIn, makeModel: makeModelIn,
      category, lifecycle, location, intune, warranty, note, activity, mismatch, vsAssignee, intuneUnassigned, arc, defender,
      assignedTo, assignedToName, sortKey, sortDir, page,
    };
    try { sessionStorage.setItem(FILTERS_KEY, JSON.stringify(s)); } catch { /* storage unavailable — non-fatal */ }
  }, [searchInput, serialIn, hostnameIn, makeModelIn, category, lifecycle, location, intune, warranty, note, activity, mismatch, vsAssignee, intuneUnassigned, arc, defender, assignedTo, assignedToName, sortKey, sortDir, page]);

  const hasActiveFilters = Boolean(
    searchInput || serialIn || hostnameIn || makeModelIn ||
    category.length || lifecycle.length || location.length || intune || warranty || note || activity || mismatch || vsAssignee || intuneUnassigned || arc || defender || assignedTo,
  );

  // Count of per-column filters active — drives the mobile "Filters (N)" badge.
  const mobileFilterCount =
    [serialIn, hostnameIn, makeModelIn, intune, warranty, note, activity].filter(Boolean).length
    + [category, lifecycle, location].filter((v) => v.length > 0).length;

  const clearFilters = () => {
    setSearchInput(''); setSerialIn(''); setHostnameIn(''); setMakeModelIn('');
    setCategory([]); setLifecycle([]); setLocation([]); setIntune('');
    setWarranty(''); setNote(''); setActivity(''); setMismatch(false); setVsAssignee(false); setIntuneUnassigned(false); setArc(''); setDefender('');
    setAssignedTo(''); setAssignedToName('');
    reset();
  };

  const locationsQuery = useQuery({ queryKey: ['locations', 'active'], queryFn: ({ signal }) => getLocations({ activeOnly: true }, signal), retry: false });
  const locationName = new Map((locationsQuery.data ?? []).map((l) => [l.LocationId, l.LocationDesc] as const));
  // State comes free with the locations lookup the page already loads — the API
  // resolves Location -> Area -> State into StateCode, so no extra request.
  const locationState = new Map((locationsQuery.data ?? []).map((l) => [l.LocationId, l.StateCode] as const));
  const stateOf = (locationId: number | null) => (locationId != null ? (locationState.get(locationId) ?? null) : null);

  // Shared filter payload (no paging) — used by both the table query and the export.
  const filters = {
    // Joined here rather than in the api client so the query key stays a plain string
    // and TanStack Query does not refetch on a new-but-equal array identity.
    category: category.join(',') || undefined,
    lifecycle: lifecycle.join(',') || undefined,
    intune,
    location: location.join(',') || undefined,
    note: note || undefined,
    activity: activity || undefined,
    primaryUserMismatch: mismatch || undefined,
    primaryUserVsAssignee: vsAssignee || undefined,
    intuneUserUnassigned: intuneUnassigned || undefined,
    arc: arc || undefined,
    defender: defender || undefined,
    warranty: warranty || undefined,
    search, serial, hostname, makeModel,
    includeDeleted: showDeleted || undefined,
    personId: assignedTo ? Number(assignedTo) : undefined,
    sort: sortKey, dir: sortDir,
  } as const;

  const restore = useMutation({
    mutationFn: (id: number) => restoreAsset(id),
    onSuccess: () => { setRestoreError(null); queryClient.invalidateQueries({ queryKey: ['assets'] }); },
    onError: (err) => setRestoreError(err instanceof ApiError ? err.message : 'Could not restore the asset.'),
  });

  const query = useQuery({
    queryKey: ['assets', { ...filters, page }],
    queryFn: ({ signal }) => listAssets({ ...filters, page, pageSize: PAGE_SIZE }, signal),
    placeholderData: keepPreviousData,
  });

  // The Defender column (Anthony, 01/11/2026: "why no badge"). One call for the page's ids, NOT part of the list query:
  // ⛔ FAIL-SOFT, a failed or empty answer leaves the column blank and never the list.
  const pageIds = (query.data?.Items ?? []).map((a) => a.AssetId);
  const badgesQuery = useQuery({
    queryKey: ['assets-defender-badges', pageIds.join(',')],
    queryFn: ({ signal }) => getAssetDefenderBadges(pageIds, signal),
    enabled: pageIds.length > 0,
    retry: false,
  });

  // Selection for bulk assign. Scoped to what is on screen: changing the filters,
  // the page or the sort clears it, so a bulk action can never touch rows the
  // user cannot currently see.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const selectionKey = JSON.stringify({ ...filters, page });
  useEffect(() => { setSelected(new Set()); }, [selectionKey]);

  // A soft-deleted asset cannot be assigned, so it is never selectable.
  const selectableIds = (query.data?.Items ?? []).filter((a) => !a.IsDeleted).map((a) => a.AssetId);
  const allOnPageSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleOne = (id: number) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllOnPage = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allOnPageSelected) selectableIds.forEach((id) => next.delete(id));
    else selectableIds.forEach((id) => next.add(id));
    return next;
  });

  const total = query.data?.Total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Export the current filter result across ALL pages (not just the visible page).
  const doExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const first = await listAssets({ ...filters, page: 1, pageSize: EXPORT_PAGE_SIZE });
      const rows = [...first.Items];
      const pages = Math.max(1, Math.ceil(first.Total / EXPORT_PAGE_SIZE));
      for (let p = 2; p <= pages; p++) {
        const next = await listAssets({ ...filters, page: p, pageSize: EXPORT_PAGE_SIZE });
        rows.push(...next.Items);
      }
      const csv = buildAssetsCsv(rows, locationName);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `oscar-assets-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <section>
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Assets</h1>
          <p className="mt-1 text-sm text-darkGrey/80">Every serialised item — laptops, mobiles, MFDs, servers, network gear and SIMs.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isAdmin && (
            <button
              type="button"
              onClick={() => { setShowDeleted((v) => !v); setPage(1); }}
              aria-pressed={showDeleted}
              className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm font-medium ${showDeleted ? 'border-orange bg-orange/10 text-navy' : 'border-border bg-white text-navy hover:bg-bg'}`}
            >
              <RotateCcw className="h-4 w-4" aria-hidden /> {showDeleted ? 'Showing deleted' : 'Show deleted'}
            </button>
          )}
          <button
            type="button"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="inline-flex items-center gap-2 rounded border border-border bg-white px-3 py-2 text-sm font-medium text-navy hover:bg-bg disabled:opacity-40"
          >
            <FilterX className="h-4 w-4" aria-hidden /> Clear filters
          </button>
          <button
            type="button"
            onClick={doExport}
            disabled={exporting || total === 0}
            className="inline-flex items-center gap-2 rounded border border-border bg-white px-3 py-2 text-sm font-medium text-navy hover:bg-bg disabled:opacity-40"
          >
            <Download className="h-4 w-4" aria-hidden /> {exporting ? 'Exporting…' : 'Export'}
          </button>
          {canMutate && (
            <Link to="/assets/new" className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90">
              <Laptop className="h-4 w-4" aria-hidden /> Add asset
            </Link>
          )}
        </div>
      </header>

      {exportError && <div role="alert" className="mb-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{exportError}</div>}
      {restoreError && <div role="alert" className="mb-3 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{restoreError}</div>}
      {showDeleted && (
        <p className="mb-3 rounded border border-orange/30 bg-orange/5 px-3 py-2 text-xs text-darkGrey/80">
          Showing soft-deleted assets. Restore one to recover it — its serial then frees up (or is reclaimed if you're re-adding the same device).
        </p>
      )}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          aria-label="Search assets"
          value={searchInput}
          onChange={(e) => { setSearchInput(e.target.value); reset(); }}
          placeholder="Search any field — serial, asset tag, hostname, make / model, IMEI, number, notes, location…"
          className="min-w-0 flex-1 basis-80 rounded border border-border bg-white px-3 py-2 text-sm"
        />
        {/* "Defender sensor deployed" = the Arc extension is on the server. It does not say Defender is healthy. */}
        <select aria-label="Filter by Azure Arc" value={arc} onChange={(e) => { setArc(e.target.value as ArcFilter); reset(); }} className="rounded border border-border bg-white px-3 py-2 text-sm">
          <option value="">Azure Arc: all assets</option>
          <option value="servers">Arc servers</option>
          <option value="defender">Defender sensor deployed</option>
          <option value="nodefender">In Arc, no Defender sensor</option>
        </select>
        {/* Intune's view of Defender on Windows devices: protection on, signatures current, nothing pending. Not a risk score. */}
        <select aria-label="Filter by Defender health" value={defender} onChange={(e) => { setDefender(e.target.value as DefenderFilter); reset(); }} className="rounded border border-border bg-white px-3 py-2 text-sm">
          <option value="">Defender: all devices</option>
          <option value="attention">Defender needs attention</option>
          <option value="healthy">Defender healthy (per Intune)</option>
          <option value="unknown">Defender not reported</option>
        </select>
      </div>

      {/* Mobile (<md): the desktop header filter row is hidden, so surface the
          same per-column filters in a collapsible panel. */}
      <MobileFilterPanel className="mb-3" activeCount={mobileFilterCount} onClear={clearFilters}>
        <MobileFilterField label="Hostname">
          <input aria-label="Filter by hostname" value={hostnameIn} onChange={(e) => { setHostnameIn(e.target.value); reset(); }} placeholder="Contains…" className={filterCls} />
        </MobileFilterField>
        <MobileFilterField label="Serial / ICCID">
          <input aria-label="Filter by serial / ICCID" value={serialIn} onChange={(e) => { setSerialIn(e.target.value); reset(); }} placeholder="Contains…" className={filterCls} />
        </MobileFilterField>
        <MobileFilterField label="Make / model">
          <input aria-label="Filter by make or model" value={makeModelIn} onChange={(e) => { setMakeModelIn(e.target.value); reset(); }} placeholder="Contains…" className={filterCls} />
        </MobileFilterField>
        <MobileFilterField label="Category">
          <MultiSelectFilter label="Filter by category" options={assetCategories.map((c) => ({ value: c, label: c }))} selected={category} onChange={(v) => { setCategory(v); reset(); }} />
        </MobileFilterField>
        <MobileFilterField label="Status">
          <MultiSelectFilter label="Filter by status" options={lifecycleStates.map((sName) => ({ value: sName, label: sName }))} selected={lifecycle} onChange={(v) => { setLifecycle(v); reset(); }} />
        </MobileFilterField>
        <MobileFilterField label="Location">
          <MultiSelectFilter label="Filter by location" options={(locationsQuery.data ?? []).map((l) => ({ value: String(l.LocationId), label: l.LocationDesc }))} selected={location} onChange={(v) => { setLocation(v); reset(); }} />
        </MobileFilterField>
        <MobileFilterField label="Intune">
          <select aria-label="Filter by Intune status" value={intune} onChange={(e) => { setIntune(e.target.value); reset(); }} className={filterCls}>
            <option value="">All</option>
            <option value="Synced">Enrolled</option>
            <option value="NotEnrolled">Not enrolled</option>
            <option value="Orphan">Orphan</option>
          </select>
        </MobileFilterField>
        <MobileFilterField label="Activity">
          <select aria-label="Filter by activity" value={activity} onChange={(e) => { setActivity(e.target.value as '' | 'active' | 'inactive'); reset(); }} className={filterCls}>
            <option value="">All</option>
            <option value="active">Active (≤30d)</option>
            <option value="inactive">Inactive</option>
          </select>
        </MobileFilterField>
        <MobileFilterField label="Warranty">
          <select aria-label="Filter by warranty" value={warranty} onChange={(e) => { setWarranty(e.target.value as '' | 'expiring' | 'expired'); reset(); }} className={filterCls}>
            <option value="">All</option>
            <option value="expiring">Expiring ≤90d</option>
            <option value="expired">Expired</option>
          </select>
        </MobileFilterField>
        <MobileFilterField label="Notes">
          <select aria-label="Filter by notes" value={note} onChange={(e) => { setNote(e.target.value as '' | 'has' | 'none'); reset(); }} className={filterCls}>
            <option value="">All</option>
            <option value="has">Has note</option>
            <option value="none">No note</option>
          </select>
        </MobileFilterField>
      </MobileFilterPanel>

      {intuneUnassigned && (
        <div className="mb-3 mr-2 inline-flex items-center gap-2 rounded-full border border-orange/40 bg-orange/10 px-3 py-1 text-xs font-semibold text-navy">
          Filtered to devices Intune has a user for that are not assigned to anyone in Oscar
          <button type="button" aria-label="Clear Intune user not assigned filter" onClick={() => { setIntuneUnassigned(false); reset(); }} className="text-orange hover:text-navy">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {vsAssignee && (
        <div className="mb-3 mr-2 inline-flex items-center gap-2 rounded-full border border-orange/40 bg-orange/10 px-3 py-1 text-xs font-semibold text-navy">
          Filtered to devices whose Intune primary user is not the person they are assigned to
          <button type="button" aria-label="Clear primary user not matching assignee filter" onClick={() => { setVsAssignee(false); reset(); }} className="text-orange hover:text-navy">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {mismatch && (
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-orange/40 bg-orange/10 px-3 py-1 text-xs font-semibold text-navy">
          Filtered to primary-user mismatches
          <button type="button" aria-label="Clear primary-user mismatch filter" onClick={() => { setMismatch(false); reset(); }} className="text-orange hover:text-navy">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Assigned-to filter. The API's personId filter matches OPEN assignments,
          so this is "what this person currently holds" — which is the list an
          offboarding hand-back works through. */}
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="block text-xs font-semibold text-darkGrey">
          Assigned to
          <div className="mt-1 w-72">
            {assignedTo && assignedToName ? (
              <div className="flex items-center gap-2">
                <span className="rounded bg-bg px-2 py-1 text-sm font-normal text-navy">{assignedToName}</span>
                <button
                  type="button"
                  onClick={() => { setAssignedTo(''); setAssignedToName(''); reset(); }}
                  className="text-xs font-normal text-red hover:underline"
                >
                  Clear
                </button>
              </div>
            ) : (
              <PersonPicker
                value={assignedTo}
                label="Filter by the person an asset is assigned to"
                placeholder="Filter by assignee — name or email"
                onChange={(v, name) => { setAssignedTo(v); setAssignedToName(name ?? ''); reset(); }}
              />
            )}
          </div>
        </label>
      </div>

      {bulkResult && (
        <div role="status" className="mb-3 rounded border border-green/30 bg-green/10 px-3 py-2 text-sm text-darkGrey">{bulkResult}</div>
      )}

      {canMutate && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded border border-orange/40 bg-orange/5 px-3 py-2">
          <span className="text-sm font-semibold text-navy">{selected.size} selected</span>
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-2 rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
          >
            <UserCheck className="h-4 w-4" aria-hidden /> Assign selected
          </button>
          <button type="button" onClick={() => setSelected(new Set())} className="text-sm font-medium text-navy hover:underline">
            Clear selection
          </button>
          <span className="text-xs text-darkGrey/70">
            Selection covers this page only — changing page, sort or filters clears it.
          </span>
        </div>
      )}

      <DesktopTable className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full min-w-[72rem] text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <th className="w-10 px-3 py-2">
                {canMutate && (
                  <input
                    type="checkbox"
                    aria-label={allOnPageSelected ? 'Deselect all on this page' : 'Select all on this page'}
                    checked={allOnPageSelected}
                    disabled={selectableIds.length === 0}
                    onChange={toggleAllOnPage}
                  />
                )}
              </th>
              <SortHeader label="Hostname" columnKey="hostname" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Serial / ICCID" columnKey="serial" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Category" columnKey="category" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Make / model" columnKey="make" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Status" columnKey="lifecycle" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Location" columnKey="location" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className="px-3 py-2">State</th>
              <SortHeader label="Intune" columnKey="intune" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Activity" columnKey="activity" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Warranty" columnKey="warranty" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className="px-3 py-2">Defender</th>
              <th className="px-3 py-2">Notes</th>
            </tr>
            {/* Per-column filter row. */}
            <tr className="border-t border-border">
              <th className="px-2 py-1.5" />
              <th className="px-2 py-1.5">
                <input aria-label="Filter by hostname" value={hostnameIn} onChange={(e) => { setHostnameIn(e.target.value); reset(); }} placeholder="Contains…" className={filterCls} />
              </th>
              <th className="px-2 py-1.5">
                <input aria-label="Filter by serial / ICCID" value={serialIn} onChange={(e) => { setSerialIn(e.target.value); reset(); }} placeholder="Contains…" className={filterCls} />
              </th>
              <th className="px-2 py-1.5">
                <MultiSelectFilter label="Filter by category" options={assetCategories.map((c) => ({ value: c, label: c }))} selected={category} onChange={(v) => { setCategory(v); reset(); }} />
              </th>
              <th className="px-2 py-1.5">
                <input aria-label="Filter by make or model" value={makeModelIn} onChange={(e) => { setMakeModelIn(e.target.value); reset(); }} placeholder="Contains…" className={filterCls} />
              </th>
              <th className="px-2 py-1.5">
                <MultiSelectFilter label="Filter by status" options={lifecycleStates.map((sName) => ({ value: sName, label: sName }))} selected={lifecycle} onChange={(v) => { setLifecycle(v); reset(); }} />
              </th>
              <th className="px-2 py-1.5">
                <MultiSelectFilter label="Filter by location" options={(locationsQuery.data ?? []).map((l) => ({ value: String(l.LocationId), label: l.LocationDesc }))} selected={location} onChange={(v) => { setLocation(v); reset(); }} />
              </th>
              {/* State is derived from the location, so it has no filter of its own. */}
              <th className="px-2 py-1.5" />
              <th className="px-2 py-1.5">
                <select aria-label="Filter by Intune status" value={intune} onChange={(e) => { setIntune(e.target.value); reset(); }} className={filterCls}>
                  <option value="">All</option>
                  <option value="Synced">Enrolled</option>
                  <option value="NotEnrolled">Not enrolled</option>
                  <option value="Orphan">Orphan</option>
                </select>
              </th>
              <th className="px-2 py-1.5">
                <select aria-label="Filter by activity" value={activity} onChange={(e) => { setActivity(e.target.value as '' | 'active' | 'inactive'); reset(); }} className={filterCls}>
                  <option value="">All</option>
                  <option value="active">Active (≤30d)</option>
                  <option value="inactive">Inactive</option>
                </select>
              </th>
              <th className="px-2 py-1.5">
                <select aria-label="Filter by warranty" value={warranty} onChange={(e) => { setWarranty(e.target.value as '' | 'expiring' | 'expired'); reset(); }} className={filterCls}>
                  <option value="">All</option>
                  <option value="expiring">Expiring ≤90d</option>
                  <option value="expired">Expired</option>
                </select>
              </th>
              {/* The Defender column is filtered by the Azure Arc and Defender dropdowns above the table. */}
              <th className="px-2 py-1.5" />
              <th className="px-2 py-1.5">
                <select aria-label="Filter by notes" value={note} onChange={(e) => { setNote(e.target.value as '' | 'has' | 'none'); reset(); }} className={filterCls}>
                  <option value="">All</option>
                  <option value="has">Has note</option>
                  <option value="none">No note</option>
                </select>
              </th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && <tr><td colSpan={COLUMN_COUNT} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>}
            {query.isError && <tr><td colSpan={COLUMN_COUNT} className="px-3 py-6 text-center text-red">Could not load assets.</td></tr>}
            {query.data?.Items.length === 0 && !query.isLoading && <tr><td colSpan={COLUMN_COUNT} className="px-3 py-6 text-center text-darkGrey/60">No assets match the filters.</td></tr>}
            {query.data?.Items.map((a) => (
              <tr key={a.AssetId} className={`border-t border-border hover:bg-bg/60 ${a.IsDeleted ? 'bg-red/5' : ''} ${selected.has(a.AssetId) ? 'bg-orange/5' : ''}`}>
                <td className="px-3 py-2">
                  {canMutate && !a.IsDeleted && (
                    <input
                      type="checkbox"
                      aria-label={`Select ${a.SerialNumber ?? a.Msisdn ?? `asset ${a.AssetId}`}`}
                      checked={selected.has(a.AssetId)}
                      onChange={() => toggleOne(a.AssetId)}
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  <Link to={`/assets/${a.AssetId}`} className="font-medium text-navy hover:underline">{a.Hostname || a.SerialNumber || a.Msisdn || '(no serial)'}</Link>
                  {a.IsDeleted && (
                    <span className="ml-2 inline-flex items-center gap-2">
                      <span className="rounded-full bg-red/15 px-2 py-0.5 text-xs font-semibold text-red">Deleted</span>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => restore.mutate(a.AssetId)}
                          disabled={restore.isPending}
                          className="text-xs font-semibold text-navy hover:underline disabled:opacity-50"
                        >
                          Restore
                        </button>
                      )}
                    </span>
                  )}
                </td>
                {/* Hostname leads (Anthony, 01/11/2026), so the serial sits second. A device with no hostname leads with its serial / number instead, and is not said twice. */}
                <td className="px-3 py-2 text-darkGrey/80">{a.Hostname ? (a.SerialNumber ?? a.Msisdn ?? '—') : '—'}</td>
                <td className="px-3 py-2 text-darkGrey/80">{a.Category}</td>
                <td className="px-3 py-2 text-darkGrey/80">{[a.Make, a.Model].filter(Boolean).join(' ') || '—'}</td>
                <td className="px-3 py-2"><span className="inline-flex rounded-full bg-lightBlue/20 px-2 py-0.5 text-xs font-semibold text-navy">{a.LifecycleState}</span></td>
                <td className="px-3 py-2 text-darkGrey/80">{a.LocationId != null ? (locationName.get(a.LocationId) ?? `#${a.LocationId}`) : '—'}</td>
                <td className="px-3 py-2 text-darkGrey/80">{stateOf(a.LocationId) ?? '—'}</td>
                <td className="px-3 py-2"><IntuneBadge status={a.IntuneStatus} /></td>
                <td className="px-3 py-2"><ActivityBadge value={a.IntuneLastLogonDate} enrolled={a.IntuneDeviceId != null} /></td>
                <td className="px-3 py-2"><WarrantyExpiry value={a.WarrantyExpiry} /></td>
                <td className="px-3 py-2"><DefenderCell badge={rowBadge(a.AssetId, badgesQuery.data)} /></td>
                <td className="px-3 py-2 text-center">{a.Notes ? <StickyNote className="inline h-4 w-4 text-darkGrey/60" aria-label="Has a note" /> : <span className="text-darkGrey/40">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>

      {/* Mobile (<md): assets as stacked cards. Per-column filters above are
          desktop-only; the top-level search/category still apply to this list. */}
      <MobileCards>
        {query.isLoading && <p className="px-1 py-6 text-center text-sm text-darkGrey/60">Loading…</p>}
        {query.isError && <p className="px-1 py-6 text-center text-sm text-red">Could not load assets.</p>}
        {query.data?.Items.length === 0 && !query.isLoading && (
          <p className="px-1 py-6 text-center text-sm text-darkGrey/60">No assets match the filters.</p>
        )}
        {query.data?.Items.map((a) => (
          <MobileCard key={a.AssetId} className={a.IsDeleted ? 'bg-red/5' : ''}>
            <div className="flex items-start justify-between gap-2">
              <span className="flex items-start gap-2">
                {canMutate && !a.IsDeleted && (
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    aria-label={`Select ${a.SerialNumber ?? a.Msisdn ?? `asset ${a.AssetId}`}`}
                    checked={selected.has(a.AssetId)}
                    onChange={() => toggleOne(a.AssetId)}
                  />
                )}
                <Link to={`/assets/${a.AssetId}`} className="text-sm font-semibold text-navy hover:text-orange hover:underline">
                  {a.Hostname || a.SerialNumber || a.Msisdn || '(no serial)'}
                </Link>
              </span>
              <span className="inline-flex shrink-0 rounded-full bg-lightBlue/20 px-2 py-0.5 text-xs font-semibold text-navy">{a.LifecycleState}</span>
            </div>
            {a.IsDeleted && (
              <div className="mt-1 flex items-center gap-2">
                <span className="rounded-full bg-red/15 px-2 py-0.5 text-xs font-semibold text-red">Deleted</span>
                {isAdmin && (
                  <button type="button" onClick={() => restore.mutate(a.AssetId)} disabled={restore.isPending} className="text-xs font-semibold text-navy hover:underline disabled:opacity-50">
                    Restore
                  </button>
                )}
              </div>
            )}
            <div className="mt-2 border-t border-border pt-2">
              <CardField label="Category">{a.Category}</CardField>
              <CardField label="Make / model">{[a.Make, a.Model].filter(Boolean).join(' ') || '—'}</CardField>
              {a.Hostname && <CardField label="Serial / ICCID">{a.SerialNumber ?? a.Msisdn ?? '—'}</CardField>}
              <CardField label="Location">{a.LocationId != null ? (locationName.get(a.LocationId) ?? `#${a.LocationId}`) : '—'}</CardField>
              <CardField label="State">{stateOf(a.LocationId) ?? '—'}</CardField>
              <CardField label="Intune"><IntuneBadge status={a.IntuneStatus} /></CardField>
              <CardField label="Activity"><ActivityBadge value={a.IntuneLastLogonDate} enrolled={a.IntuneDeviceId != null} /></CardField>
              <CardField label="Defender"><DefenderCell badge={rowBadge(a.AssetId, badgesQuery.data)} /></CardField>
              <CardField label="Warranty"><WarrantyExpiry value={a.WarrantyExpiry} /></CardField>
            </div>
          </MobileCard>
        ))}
      </MobileCards>

      <BulkAssignDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        assetIds={[...selected]}
        onDone={(result) => {
          setSelected(new Set());
          queryClient.invalidateQueries({ queryKey: ['assets'] });
          setBulkResult(
            result.SkippedAssetIds.length > 0
              ? `Assigned ${result.Updated} of ${result.Requested}. ${result.SkippedAssetIds.length} could not be found and were skipped.`
              : `Assigned ${result.Updated} ${result.Updated === 1 ? 'asset' : 'assets'}.`,
          );
        }}
      />

      <div className="mt-3 flex items-center justify-between text-sm text-darkGrey/80">
        <span>{total} {total === 1 ? 'asset' : 'assets'}</span>
        <div className="flex items-center gap-2">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded border border-border bg-white px-2 py-1 disabled:opacity-40">Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded border border-border bg-white px-2 py-1 disabled:opacity-40">Next</button>
        </div>
      </div>
    </section>
  );
}

/** The Defender label for a row: Arc's sensor state for a server, Intune's health for a Windows device. Blank for the rest. */
function DefenderCell({ badge }: { badge: RowBadge | null }) {
  if (!badge) return <span className="text-darkGrey/40">—</span>;
  const cls = badge.tone === 'ok'
    ? 'bg-navy/10 text-navy'
    : badge.tone === 'warn'
      ? 'bg-orange/15 text-navy'
      : 'bg-darkGrey/10 text-darkGrey';
  return <span title={badge.title} className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{badge.text}</span>;
}

/** Intune enrolment indicator from the asset's IntuneStatus (set by the sync). */
function IntuneBadge({ status }: { status: string | null }) {
  if (status === 'Synced') return <span className="inline-flex rounded-full bg-green/15 px-2 py-0.5 text-xs font-semibold text-green">Enrolled</span>;
  if (status === 'NotEnrolled') return <span className="inline-flex rounded-full bg-darkGrey/10 px-2 py-0.5 text-xs font-semibold text-darkGrey">Not enrolled</span>;
  if (status === 'Orphan') return <span className="inline-flex rounded-full bg-orange/15 px-2 py-0.5 text-xs font-semibold text-orange">Orphan</span>;
  return <span className="text-darkGrey/50">—</span>;
}

/** Active/Inactive pill from the last interactive logon (migration 023). */
function ActivityBadge({ value, enrolled }: { value: string | null; enrolled: boolean }) {
  if (!value) return <span className="text-darkGrey/40">{enrolled ? 'No data' : '—'}</span>;
  return isActiveDevice(value)
    ? <span className="inline-flex rounded-full bg-green/15 px-2 py-0.5 text-xs font-semibold text-green">Active</span>
    : <span className="inline-flex rounded-full bg-darkGrey/10 px-2 py-0.5 text-xs font-semibold text-darkGrey">Inactive</span>;
}
